import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../api/client';
import { useChatHistory } from '../hooks/useChatHistory';

interface ApiConnection {
  platform: string;
  api_key: string;
  created_at: string;
}

export function Deployments() {
  const { user } = useAuth();
  const isPaidUser = user?.role === 'admin' || (user as any)?.is_paid;
  const { projects } = useChatHistory();
  const [connections, setConnections] = useState<ApiConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('fly');
  const [apiKey, setApiKey] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      loadConnections();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0].id);
    }
  }, [projects, selectedProject]);

  const loadConnections = async () => {
    try {
      const res = await apiFetch('/api/deploy/connections');
      setConnections(res.connections || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveConnection = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await apiFetch('/api/deploy/connections', {
        method: 'POST',
        body: JSON.stringify({ platform, api_key: apiKey }),
      });
      setSuccess(`Successfully saved ${platform} connection!`);
      setApiKey('');
      loadConnections();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Mock project deploy function
  const triggerDeploy = async (targetPlatform: string) => {
    if (!isPaidUser) {
      alert('Hãy nâng cấp tài khoản trả phí để sử dụng tính năng này');
      return;
    }
    setError('');
    setSuccess('');
    try {
      const res = await apiFetch('/api/deploy/run', {
        method: 'POST',
        body: JSON.stringify({ project_id: selectedProject || '00000000-0000-0000-0000-000000000000', platform: targetPlatform }),
      });
      setSuccess(`Deployment to ${targetPlatform} started! ID: ${res.deployment_id}`);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-2">Deployments</h1>
      <p className="text-sm text-gray-400 mb-8">
        Quản lý triển khai hệ thống Ericloud và cấu hình API Keys.
      </p>

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 text-green-400 rounded">{success}</div>}

      {/* ADMIN VIEW */}
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#1C1C1F] border border-[#2D2D30] rounded-xl p-5">
            <h2 className="text-lg font-semibold mb-4">Add API Connection</h2>
            <form onSubmit={saveConnection} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Platform</label>
                <select 
                  className="w-full bg-[#0D0D0E] border border-[#2D2D30] rounded p-2 text-sm text-white"
                  value={platform} onChange={e => setPlatform(e.target.value)}
                >
                  <option value="fly">Fly.io</option>
                  <option value="neon">Neon.tech</option>
                  <option value="cloudflare">Cloudflare</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">API Key</label>
                <input 
                  type="password" required
                  className="w-full bg-[#0D0D0E] border border-[#2D2D30] rounded p-2 text-sm text-white placeholder-gray-600"
                  placeholder="Enter secret token..."
                  value={apiKey} onChange={e => setApiKey(e.target.value)}
                />
              </div>
              <button type="submit" className="bg-[#5865F2] hover:bg-[#4752C4] text-white rounded p-2 text-sm font-medium transition-colors">
                Save Connection
              </button>
            </form>
          </div>

          <div className="bg-[#1C1C1F] border border-[#2D2D30] rounded-xl p-5">
            <h2 className="text-lg font-semibold mb-4">Saved Connections</h2>
            {connections.length === 0 ? (
              <p className="text-sm text-gray-500">No connections configured yet.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {connections.map(c => (
                  <div key={c.platform} className="flex justify-between items-center p-3 bg-[#0D0D0E] rounded border border-[#2D2D30]">
                    <div>
                      <div className="font-medium capitalize text-sm">{c.platform}</div>
                      <div className="text-xs text-gray-500">Added {new Date(c.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-xs font-mono text-gray-400 bg-black px-2 py-1 rounded">
                      {c.api_key}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* USER & ADMIN DEPLOY OPTIONS */}
      <div className="bg-[#1C1C1F] border border-[#2D2D30] rounded-xl p-5">
        <h2 className="text-lg font-semibold mb-4">Run Deployment</h2>
        
        {/* Project Selector */}
        <div className="mb-6">
           <label className="block text-sm font-medium text-gray-300 mb-2">Chọn Dự án (Project)</label>
           <select 
             className="w-full md:w-1/2 bg-[#0D0D0E] border border-[#2D2D30] rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500"
             value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
           >
             {projects.length === 0 && <option value="">Không có dự án nào</option>}
             {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
           </select>
        </div>

        <div className="flex gap-4 flex-wrap">
          {isAdmin ? (
            <>
              <button 
                onClick={() => triggerDeploy('ericloud')}
                className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white rounded-lg p-4 text-left shadow-lg shadow-blue-500/20 transition-all"
              >
                <div className="font-bold text-lg mb-1">Deploy to Ericloud ☁️</div>
                <div className="text-xs text-blue-100">Build .APK, .EXE, .ZIP & Host on Hetzner KVM</div>
              </button>
              
              <button 
                onClick={() => triggerDeploy('fly')}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white rounded-lg p-4 text-left shadow-lg shadow-purple-500/20 transition-all"
              >
                <div className="font-bold text-lg mb-1">Deploy Webapp 🚀</div>
                <div className="text-xs text-purple-100">Fly.io (Backend) + Cloudflare (CDN) + Neon (DB)</div>
              </button>
            </>
          ) : (
            <button 
              onClick={() => {
                if (!isPaidUser) {
                  setError('Hãy nâng cấp tài khoản trả phí để sử dụng tính năng này');
                  setSuccess('');
                } else {
                  triggerDeploy('zip');
                }
              }}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white rounded-lg p-4 text-left shadow-lg shadow-emerald-500/20 transition-all"
            >
              <div className="font-bold text-lg mb-1">📦 Đóng gói Project</div>
              <div className="text-sm text-emerald-100">Đóng gói Project thành file zip và xuất bản</div>
            </button>
          )}
        </div>
      </div>

    </div>
  );
}
